import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useLocation } from "@reach/router";
import { useStaticQuery, graphql } from "gatsby";

// https://www.gatsbyjs.com/docs/add-page-metadata/
// https://www.gatsbyjs.com/docs/add-seo-component/
// https://www.gatsbyjs.com/docs/how-to/adding-common-features/seo/
// https://www.gatsbyjs.com/tutorial/seo-and-social-sharing-cards-tutorial/#using-the-seo-component

export default function SEO({ title, description, image, article, localBusiness }) {
  const { pathname } = useLocation();
  const { site } = useStaticQuery(query);

  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
    tel,
    street,
    streetNumber,
    zipCode,
    city,
    country,
  } = site.siteMetadata;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image || defaultImage}`,
    url: `${siteUrl}${pathname}`,
  };

  const localBusinessSchema = localBusiness !== false ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "A & F Garten- und Landschaftsbau",
    "description": "Professioneller Garten- und Landschaftsbau in Flensburg, Schleswig, Glücksburg und Umgebung. Heckenschnitt, Rasenmähen, Pflasterarbeiten, Baumschnitt und ganzjährige Gartenpflege.",
    "url": siteUrl,
    "telephone": tel,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": `${street} ${streetNumber}`,
      "addressLocality": city,
      "postalCode": zipCode,
      "addressCountry": country === "Deutschland" ? "DE" : country,
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 54.7834,
      "longitude": 9.4372,
    },
    "areaServed": [
      "Flensburg", "Harrislee", "Handewitt", "Glücksburg", "Wees",
      "Tastrup", "Engelsby", "Weiche", "Mürwik", "Tarup",
      "Jarplund-Weding", "Freienwill", "Schleswig", "Kappeln",
      "Sörup", "Langballig", "Gelting", "Satrup", "Eggebek",
      "Schafflund", "Steinbergkirche",
    ],
    "priceRange": "€€",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
        "opens": "07:00",
        "closes": "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "08:00",
        "closes": "14:00",
      },
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "7",
      "bestRating": "5",
      "worstRating": "1",
    },
    "hasMap": "https://share.google/qgDxVg4F9bDVb8KNp",
    "sameAs": [
      "https://share.google/qgDxVg4F9bDVb8KNp",
    ],
  }) : null;

  return (
    <>
    <Helmet title={seo.title} titleTemplate={titleTemplate}>
      <html lang="de" />
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />

      {seo.url && <meta property="og:url" content={seo.url} />}

      {(article ? true : null) && <meta property="og:type" content="article" />}

      {seo.title && <meta property="og:title" content={seo.title} />}

      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}

      {seo.image && <meta property="og:image" content={seo.image} />}

      <meta name="twitter:card" content="summary_large_image" />

      {twitterUsername && (
        <meta name="twitter:creator" content={twitterUsername} />
      )}

      {seo.title && <meta name="twitter:title" content={seo.title} />}

      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}

      {seo.image && <meta name="twitter:image" content={seo.image} />}

    </Helmet>
    {localBusinessSchema && (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: localBusinessSchema }}
      />
    )}
  </>
  );
}

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
  localBusiness: PropTypes.bool,
};

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  article: false,
  localBusiness: true,
};

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        twitterUsername
        tel
        street
        streetNumber
        zipCode
        city
        country
      }
    }
  }
`;
